import { useState, useEffect } from 'react';
import { ShoppingCart, IndianRupee, TrendingUp, Package, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { sellerAPI } from '@/lib/api';

export default function Statistics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await sellerAPI.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Statistics</h1>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-20 mb-4" />
                <div className="h-8 bg-muted rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Total Orders', 
      value: stats?.total_orders || 0, 
      icon: ShoppingCart,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    { 
      title: 'Pending', 
      value: stats?.pending_orders || 0, 
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    { 
      title: 'Accepted', 
      value: stats?.accepted_orders || 0, 
      icon: Package,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    { 
      title: 'Rejected', 
      value: stats?.rejected_orders || 0, 
      icon: Users,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10'
    },
  ];

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Statistics</h1>
        <p className="text-muted-foreground mt-1">Track your shop performance</p>
      </div>

      {/* Order Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`h-8 w-8 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-secondary" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-heading font-bold text-secondary">
              ₹{(stats?.total_revenue || 0).toLocaleString()}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              From accepted orders
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-heading font-bold">
              {stats?.monthly_orders || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Orders · ₹{(stats?.monthly_revenue || 0).toLocaleString()} revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Catalog Stats */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-heading">Catalog Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-muted-foreground">Total Products</p>
              <p className="text-3xl font-heading font-bold">{stats?.products_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Categories</p>
              <p className="text-3xl font-heading font-bold">{stats?.categories_count || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
